import React from 'react';
import Link from 'redux-first-router-link';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import cartAnalytics from './cartAnalytics';
import { changeQuantity, addToBag } from '../../actions/checkout';
import Price from '../Product/Price';
import { translate, translateObjectData, prefix } from '../../utils/locale';
import { deepClone } from '../../utils/object';
import { urlify, deCamel, atob } from '../../utils/string';
import s from './CartItems.scss';

const CartItems = ({ checkout, currentPage, setup, products, toggleCartHandler, ...props }) => {
	const updateQuantityHandler = (e) => {
		e.preventDefault();
		const $link = e.currentTarget;
		const itemId = $link.closest('.u_checkoutItem').dataset.line;
		const itemObj = checkout.selection.items.reduce((acc, item) => (item.line === itemId ? { ...acc, ...item } : acc), {});
		const action = $link.value;
		let cachedItem = checkout.selectionCache.filter((scItem) => atob(scItem.selectionVariantId) === atob(itemObj.id))[0];
		if (!cachedItem) {
			cachedItem = deepClone(products[itemObj.cacheId]);
			cachedItem.selectionVariantId = itemObj.id;
			cachedItem.category = cachedItem.canonicalUri.slice(0, cachedItem.canonicalUri.lastIndexOf('/'));
		}
		let quantity = itemObj.quantity;

		switch (action) {
			case 'increase':
				quantity += 1;
				break;
			case 'decrease':
				quantity -= 1;
				break;
			case 'remove':
				quantity = 0;
				break;
			default:
				break;
		}

		props.changeQuantity([{ id: itemObj.line, quantity }], cachedItem, cachedItem.category).then((response) => {
			cartAnalytics([{ merchandiseId: itemObj.id }], [cachedItem], setup, cachedItem.category, `${parseInt(response?.data?.totalPriceV2?.amount || 0, 10)}`, ['remove', 'decrease'].indexOf(action) !== -1, quantity);
			return response;
		}).catch(() => console.warn('Cart analytics not triggered'));
	};

	/*
	const setQuantityHandler = (id, quantity) => {
		const itemId = id.replace('updateQuantity-', '');
		const itemObj = checkout.selection.items.reduce((acc, item) => (item.line === itemId ? { ...acc, ...item } : acc), {});
		let cachedItem = checkout.selectionCache.filter((scItem) => scItem.selectionVariantId === itemObj.id)[0];
		if (!cachedItem) {
			cachedItem = deepClone(products[itemObj.cacheId]);
			cachedItem.selectionVariantId = itemObj.id;
			cachedItem.category = cachedItem.canonicalUri.slice(0, cachedItem.canonicalUri.lastIndexOf('/'));
		}
		quantity = parseInt(quantity, 10);

		props.changeQuantity([{ id: itemObj.line, quantity }], [cachedItem], cachedItem.category).then((response) => {
			cartAnalytics([{ merchandiseId: itemObj.id }], [cachedItem], setup, cachedItem.category, `${parseInt(response?.data?.totalPriceV2?.amount || 0, 10)}`, cachedItem.quantity < quantity, quantity);
			return response;
		}).catch(() => console.warn('Cart analytics not triggered'));
	};
	*/

	const lineItems = checkout.selection.items ? checkout.selection.items
		.sort((a, b) => {
			let aIndex = 0;
			let bIndex = 0;
			checkout.selectionCache.forEach((item, i) => {
				if (a.id === item.selectionVariantId) aIndex = i;
				if (b.id === item.selectionVariantId) bIndex = i;
			});
			return aIndex > bIndex ? 1 : -1;
		})
		.map((item) => {
			const itemCache = checkout.selectionCache.filter((scItem) => atob(scItem.selectionVariantId) === atob(item.id))[0];
			if (item.quantity === 0 || !itemCache?.name) return null;
			const itemDetails = translateObjectData(setup, itemCache);
			const itemName = `${itemDetails.name}${(itemDetails.collection || '').indexOf('by') !== -1 ? ` ${itemDetails.collection.slice(itemDetails.collection.indexOf('by'))}` : ''}`;

			return (
				<div key={item.line} data-line={item.line} className={[s.cartItem, 'u_checkoutItem'].join(' ')}>
					<Link className={s.cartItem_image} to={prefix(setup, itemDetails.canonicalUri)} onClick={toggleCartHandler}>
						{ itemDetails.media.filter((mediaItem) => mediaItem.type === 'IMAGE').slice(0, 1).map((mediaItem) => <img key={mediaItem.src} src={mediaItem.src.replace(/\.([^.]+)$/, '_200x.$1')} alt={mediaItem.alt} />) }
					</Link>
					<div className={s.cartItem_info}>
						<span className={s.cartItem_info_name}>
							{ itemName }
							<br />
							<Price price={item.price} setup={setup} checkout />
						</span>
						<dl className={s.cartItem_info_items}>
							{
								Object.keys(item.selectedOptions).map((option) => (
									<div key={option} className={s.cartItem_info_item}>
										<dt>{ setup.statics.variantTypes.filter((vT) => urlify(vT.type) === urlify(option)).map((vT) => translate(setup, vT.label)).join('') || deCamel(option) }</dt>
										<dd>{ item.selectedOptions[option] }</dd>
									</div>
								))
							}
							<div className={s.cartItem_info_item}>
								<dt>{ translate(setup, 'sku') }</dt>
								<dd>{ item.sku }</dd>
							</div>
						</dl>
						{
							currentPage !== 'receipt' ? (
								<div className={s.cartItem_info_quantity} role="group" aria-label={`${translate(setup, 'quantity')} for ${itemName}: ${item.quantity}`}>
									<button className={s.cartItem_info_quantity_adjust} type="button" aria-label="Decrease quantity" value="decrease" onClick={updateQuantityHandler} disabled={item.quantity <= 1} />
									<span className={s.cartItem_info_quantity_total}>{ item.quantity }</span>
									<button className={[s.cartItem_info_quantity_adjust, s.cartItem_info_quantity_adjust__increase].join(' ')} type="button" aria-label="Increase quantity" value="increase" onClick={updateQuantityHandler} />
								</div>
							) : <span className={s.cartItem_info_quantity}>{ item.quantity }</span>
						}
					</div>
					<button className={s.cartItem_remove} onClick={updateQuantityHandler} value="remove" type="button" aria-label={`Remove ${itemName}`} />
				</div>
			);
		}) : [];

	return (
		<div className={s.cartItems}>
			{ lineItems }
		</div>
	);
};

const mapState = ({ environment, checkout }, { currentPage, products }) => ({ checkout, setup: environment.setup, products, currentPage });

export default hot(module)(connect(mapState, { changeQuantity, addToBag })(CartItems));
